import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiService,
  CreateFlyerTemplateData,
  UpdateFlyerTemplateData,
} from '../services/api';
import toast from 'react-hot-toast';

export const useFlyerTemplates = () => {
  return useQuery({
    queryKey: ['flyer-templates'],
    queryFn: apiService.getFlyerTemplates,
  });
};

export const useFlyerCategories = () => {
  return useQuery({
    queryKey: ['flyer-categories'],
    queryFn: apiService.getFlyerCategories,
  });
};

export const useCreateFlyerTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFlyerTemplateData) =>
      apiService.createFlyerTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer-templates'] });
      queryClient.invalidateQueries({ queryKey: ['flyer-categories'] });
      toast.success('Template created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create template');
    },
  });
};

export const useUpdateFlyerTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFlyerTemplateData }) =>
      apiService.updateFlyerTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer-templates'] });
      queryClient.invalidateQueries({ queryKey: ['flyer-categories'] });
      toast.success('Template updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update template');
    },
  });
};

export const useToggleFlyerTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiService.toggleFlyerTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer-templates'] });
      toast.success('Template status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to toggle template');
    },
  });
};

export const useToggleFeatureFlyerTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiService.toggleFeatureFlyerTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer-templates'] });
      toast.success('Template featured status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update template');
    },
  });
};

export const useDeleteFlyerTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiService.deleteFlyerTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer-templates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete template');
    },
  });
};

export const useUploadFlyerTemplateFile = () => {
  return useMutation({
    mutationFn: (file: File) => apiService.uploadFlyerTemplateFile(file),
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload file');
    },
  });
};
